import { useMemo } from 'react';
import {
  canAccessView,
  canAssignPlatformRoles,
  canPerformAction,
  getAssignablePlatformRoles,
  getRequestableOrganizationFields,
  getRequestableOrganizationUserFields,
  getRequestableUserByIdFields,
  getRequestableUserFields,
  getViewerRoles,
  isReadOnlyViewer,
  isOrganizationFieldVisible,
  isUserFieldVisible,
} from '@/lib/authZ';
import type { IViewerContext } from '@/lib/authZ';

/**
 * React hook that memoizes the viewer's roles and exposes authorization helpers
 * bound to the current session. Use this instead of calling JWT helpers directly.
 */
const useAuthorization = () => {
  const viewerRoles = useMemo(() => getViewerRoles(), []);

  return useMemo(() => ({
    viewerRoles,
    canPerformAction: (
      action: Parameters<typeof canPerformAction>[0],
      context: IViewerContext = {},
    ) => (
      canPerformAction(action, context, viewerRoles)
    ),
    canAccessView: (view: Parameters<typeof canAccessView>[0]) => (
      canAccessView(view, viewerRoles)
    ),
    isReadOnlyViewer: () => isReadOnlyViewer(viewerRoles),
    getAssignablePlatformRoles: () => getAssignablePlatformRoles(viewerRoles),
    canAssignPlatformRoles: () => canAssignPlatformRoles(viewerRoles),
    getRequestableUserFields: (context: IViewerContext = {}) => (
      getRequestableUserFields(context, viewerRoles)
    ),
    getRequestableUserByIdFields: (context: IViewerContext = {}) => (
      getRequestableUserByIdFields(context, viewerRoles)
    ),
    getRequestableOrganizationUserFields: (context: IViewerContext = {}) => (
      getRequestableOrganizationUserFields(context, viewerRoles)
    ),
    getRequestableOrganizationFields: (context: IViewerContext = {}) => (
      getRequestableOrganizationFields(context, viewerRoles)
    ),
    isUserFieldVisible: (
      fieldName: Parameters<typeof isUserFieldVisible>[0],
      context: IViewerContext = {},
    ) => (
      isUserFieldVisible(fieldName, context, viewerRoles)
    ),
    isOrganizationFieldVisible: (
      fieldName: Parameters<typeof isOrganizationFieldVisible>[0],
      context: IViewerContext = {},
    ) => isOrganizationFieldVisible(fieldName, context, viewerRoles),
  }), [viewerRoles]);
};

export default useAuthorization;
